import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, CardContent, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import UploadIcon from '@mui/icons-material/Upload';
import { FormProvider, RHFFileField, RHFSearchableSelectField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { apiConstants, post } from "../../common/constants";
import { useSnackbar } from "notistack";
import PageTitle from "../../components/PageTitle";
import _ from 'lodash';
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import { formatDate } from "../../helpers/UtilityHelper";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import MUIModal from "../../components/MuiModal";
import { FieldLabel, FilterField } from "../../common/FilterBar";
import CopyIcon from "../../common/CopyIcon";

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold }) => (
    <Box sx={{ fontSize: 12.5, lineHeight: 1.4, color: muted ? 'text.secondary' : 'text.primary', fontWeight: bold ? 600 : 400 }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

function ProgressBadge({ status }) {
    const ok = status === 'COMPLETED';
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px',
            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
            bgcolor: ok ? '#f0fdf4' : '#fffbeb',
            color: ok ? '#16a34a' : '#b45309',
            border: `1px solid ${ok ? '#bbf7d0' : '#fde68a'}`,
        }}>
            {status || '—'}
        </Box>
    );
}

function ViewButton({ onClick, label }) {
    return (
        <Box component="button" onClick={onClick}
            sx={{
                display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                bgcolor: 'transparent', color: '#2563eb', border: '1px solid #bfdbfe',
                transition: 'all 0.15s', '&:hover': { bgcolor: '#eff6ff', borderColor: '#93c5fd' },
            }}>
            {label}
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function B2bCreateUpload() {
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();
    const { enqueueSnackbar } = useSnackbar();
    const sampleName = 'B2B_BULK_ORDER_V1';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const methods = useForm();
    const { handleSubmit, reset } = methods;
    const [seeOrders, setSeeOrders] = useState(false);
    const [orderData, setOrderData] = useState([]);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [page1, setPage1] = useState(0);
    const [sizePerPage1, setSizePerPage1] = useState(10);

    useEffect(() => {
        post(apiConstants.REPORT_GENERIC_SEARCH, {
            "collection": "bulk_orders_result",
            "offset": (page) * sizePerPage,
            "limit": sizePerPage,
            "andfilter": [{ "field": "type", "operator": "eq", "value": "B2B_ORDER" }],
            "sort": { "field": "time", "direction": "DESC" }
        }).then((res) => { setData(res?.data?.data); });
    }, [sizePerPage, page]);

    const onSubmit = async (data) => {
        if (!data.merchant) { enqueueSnackbar('Please select a merchant', { variant: 'error' }); return false; }
        setIsSubmitting(true);
        let formData = new FormData();
        formData.append('file', data?.file[0]);
        try {
            await post(apiConstants.B2B_BULK_ORDER_CREATE + '?merchantOId=' + data.merchant, formData).then((res) => {
                if (res.status === 200) { enqueueSnackbar('Successfully uploaded!', { variant: 'success' }); reset(); }
                else { enqueueSnackbar(`${res.data}`, { variant: 'error' }); reset(); }
            });
            reset();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || err.message, { variant: 'error' });
            reset();
        }
        setIsSubmitting(false);
    };

    const columns = [
        {
            field: '_id',
            headerName: 'ID', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 200, flex: 1,
            renderCell: ({ row }) => <CellLine muted>{row._id}</CellLine>,
        },
        {
            field: 'merchantId',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => {
                const m = merchants?.find(item => item.merchantId === row.merchantId);
                return <CellLine bold>{m?.name || '—'}</CellLine>;
            },
        },
        {
            field: 'file',
            headerName: 'File', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 80,
            renderCell: ({ row }) => {
                if (!row?.s3RefToFile?.signedLink) return <CellLine muted>—</CellLine>;
                return (
                    <Box component="a" href={row.s3RefToFile.signedLink} target="_blank" rel="noreferrer"
                        sx={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'none', borderBottom: '1px dashed #93c5fd', '&:hover': { borderBottomStyle: 'solid' } }}>
                        View
                    </Box>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 140,
            renderCell: ({ row }) => <ProgressBadge status={row?.status} />,
        },
        {
            field: 'createdAt',
            headerName: 'Upload Date', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 130, flex: 0.9,
            renderCell: ({ row }) => <CellLine muted>{formatDate(row?.time)}</CellLine>,
        },
        {
            field: 'totalRecords',
            headerName: 'Upload Stats', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <Box sx={{ fontSize: '12px', color: 'text.primary' }}><DataLabel>Total</DataLabel>{row?.totalOrders ?? 0}</Box>
                    <Box sx={{ fontSize: '12px', color: '#16a34a' }}><DataLabel>Success</DataLabel>{row?.successOrders?.length ?? 0}</Box>
                    <Box sx={{ fontSize: '12px', color: '#dc2626' }}><DataLabel>Failed</DataLabel>{row?.failedOrders?.length ?? 0}</Box>
                </CellStack>
            ),
        },
        {
            field: 'successOrders',
            headerName: 'Successful', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 140,
            renderCell: ({ row }) => {
                if (!row?.successOrders?.length) return <CellLine muted>—</CellLine>;
                return <ViewButton label={`View (${row.successOrders.length})`} onClick={() => { setOrderData(row.successOrders); setSeeOrders(true); }} />;
            },
        },
        {
            field: 'failedOrders',
            headerName: 'Failed', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 110,
            renderCell: ({ row }) => {
                if (!row?.failedOrders?.length) return <CellLine muted>—</CellLine>;
                return <ViewButton label={`View (${row.failedOrders.length})`} onClick={() => { setOrderData(row.failedOrders); setSeeOrders(true); }} />;
            },
        },
    ];

    const columnsOrderModal = [
        { field: "reference", headerAlign: "left", hideSortIcons: true, headerName: "Reference No", flex: 0.7, renderCell: ({ row }) => row?.data?.reference },
        { field: "awb", headerAlign: "left", hideSortIcons: true, headerName: "AWB", flex: 0.7, renderCell: ({ row }) => <Box sx={{ display: 'flex', alignItems: 'center' }}>{row?.data?.awbNumber}<CopyIcon text={row?.data?.awbNumber} /></Box> },
        { field: "reason", headerAlign: "left", hideSortIcons: true, headerName: "Remark", flex: 2, renderCell: ({ row }) => row?.data?.reason },
    ];

    return (
        <>
            <PageTitle>Upload PTL Orders Manifestation (Generate Labels)</PageTitle>

            {/* ── Upload form ───────────────────────────────────────────── */}
            <Card elevation={0} sx={{ mb: 2, borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Upload Orders for Manifestation</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>Select a merchant and upload a CSV file to generate labels in bulk</Typography>
                </Box>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={6}>
                                <RHFSearchableSelectField
                                    width="100%"
                                    label="Select Merchant"
                                    options={_.orderBy(merchants, 'name').map(val => ({ value: val.id, label: val.name }))}
                                    name="merchant"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <RHFFileField name='file' required={true} fileType={['csv']} label="Click to upload CSV file" />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        variant="contained"
                                        size="small"
                                        startIcon={<UploadIcon />}
                                        type="submit"
                                        disableElevation
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                                    >
                                        Upload
                                    </LoadingButton>
                                    <DownloadSampleFile fileName={sampleName} label="Sample" />
                                </Box>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Upload history ────────────────────────────────────────── */}
            <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Upload History</Typography>
                </Box>
                <Box sx={{ bgcolor: '#fff', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                    <ProzoDataGrid
                        columns={columns}
                        rows={data || []}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        pagination={true}
                        hideFooterPagination={false}
                        filterMode={"server"}
                        getRowHeight={() => 'auto'}
                        rowCount={Number.MAX_VALUE}
                        hideCountNumber={true}
                        getRowId={(row) => row._id}
                    />
                </Box>
            </Card>

            <MUIModal open={seeOrders} title="Upload Details" maxWidth={"lg"} handleClose={() => setSeeOrders(false)}>
                <ProzoDataGrid
                    sizePerPage={sizePerPage1}
                    setSizePerPage={setSizePerPage1}
                    page={page1}
                    setPage={setPage1}
                    rowCount={orderData?.length}
                    columns={columnsOrderModal}
                    rows={orderData}
                    paginationMode={'client'}
                    pagination={true}
                    hideFooterPagination={false}
                    getRowId={() => Math.random()}
                    getRowHeight={() => 30}
                />
            </MUIModal>
        </>
    );
}
